const PUBLIC_URL = process.env.PUBLIC_URL || '';
const SERVICE_WORKER_PATH = `${PUBLIC_URL}/notification-sw.js`;
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const LOCALHOSTS = new Set(['localhost', '127.0.0.1', '::1']);

const PUSH_OPT_OUT_KEY = 'pushOptOut';
const PUSH_RESYNC_MIN_INTERVAL_MS = 30 * 60 * 1000;

let registrationPromise = null;
let lastPushResyncAt = 0;

const isBrowser = () => typeof window !== 'undefined' && typeof navigator !== 'undefined';

const hasNotificationApi = () => isBrowser() && 'Notification' in window;

const hasServiceWorkerApi = () => isBrowser() && 'serviceWorker' in navigator;

const hasPushApi = () => isBrowser() && 'PushManager' in window;

const isStandaloneMode = () => {
    if (!isBrowser()) return false;

    const mediaStandalone =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = window.navigator.standalone === true;

    return mediaStandalone || iosStandalone;
};

const isSecureForServiceWorker = () => {
    if (!isBrowser()) return false;
    if (window.isSecureContext) return true;

    return LOCALHOSTS.has(window.location.hostname);
};

export const getNotificationDiagnostics = () => {
    if (!isBrowser()) {
        return {
            supported: false,
            hasNotificationApi: false,
            hasServiceWorkerApi: false,
            hasPushApi: false,
            isSecureContext: false,
            isStandalone: false,
            permission: 'unsupported',
            serviceWorkerRegistered: false,
            isIOS: false,
        };
    }

    const userAgent = window.navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/i.test(userAgent);

    return {
        supported: hasNotificationApi(),
        hasNotificationApi: hasNotificationApi(),
        hasServiceWorkerApi: hasServiceWorkerApi(),
        hasPushApi: hasPushApi(),
        isSecureContext: isSecureForServiceWorker(),
        isStandalone: isStandaloneMode(),
        permission: hasNotificationApi() ? Notification.permission : 'unsupported',
        serviceWorkerRegistered: false,
        isIOS,
    };
};

export const initNotificationServiceWorker = async () => {
    if (!hasServiceWorkerApi() || !isSecureForServiceWorker()) {
        return null;
    }

    if (!registrationPromise) {
        registrationPromise = navigator.serviceWorker.register(SERVICE_WORKER_PATH).catch((error) => {
            registrationPromise = null;
            throw error;
        });
    }

    return registrationPromise;
};

export const refreshServiceWorkerRegistrationState = async (diagnostics) => {
    if (!hasServiceWorkerApi()) {
        return diagnostics;
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
        return {
            ...diagnostics,
            serviceWorkerRegistered: Boolean(registration),
        };
    } catch {
        return {
            ...diagnostics,
            serviceWorkerRegistered: false,
        };
    }
};

export const requestBrowserNotificationPermission = async () => {
    if (!hasNotificationApi()) {
        return 'unsupported';
    }

    return Notification.requestPermission();
};

export const sendBrowserTestNotification = async (payload = {}) => {
    if (!hasNotificationApi()) {
        throw new Error('Notification API is not supported in this browser.');
    }

    if (Notification.permission !== 'granted') {
        throw new Error('Notification permission is not granted.');
    }

    const title = payload.title || 'FPC CRM';
    const body = payload.body || 'This is a test browser notification from FPC CRM.';
    const targetUrl = payload.targetUrl || '/dashboard';

    const options = {
        body,
        icon: `${PUBLIC_URL}/app-icons/icon-192.png`,
        badge: `${PUBLIC_URL}/app-icons/icon-192.png`,
        tag: 'fpc-crm-browser-notification-test',
        renotify: true,
        requireInteraction: false,
        data: {
            url: targetUrl,
            createdAt: Date.now(),
        },
    };

    const registration = await initNotificationServiceWorker();
    if (registration && typeof registration.showNotification === 'function') {
        await registration.showNotification(title, options);
        return { channel: 'service-worker' };
    }

    // Fallback for browsers without service worker notification support.
    new Notification(title, options);
    return { channel: 'window-notification' };
};

// ---------------------------------------------------------------------------
// Server-side Web Push (VAPID)
// ---------------------------------------------------------------------------

function _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

function _subscriptionUsesVapidKey(pushSubscription, vapidPublicKey) {
    const currentKey = pushSubscription?.options?.applicationServerKey;
    if (!currentKey) return false;

    const expected = _urlBase64ToUint8Array(vapidPublicKey);
    const actual = new Uint8Array(currentKey);
    if (actual.length !== expected.length) return false;
    return actual.every((value, index) => value === expected[index]);
}

function _authHeaders() {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Необходима авторизация.');
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
}

const savePushSubscriptionOnServer = async (pushSubscription) => {
    if (!pushSubscription) {
        throw new Error('Push-подписка браузера отсутствует.');
    }

    const subscriptionJson = pushSubscription.toJSON();
    const res = await fetch(`${API_BASE_URL}/push-subscriptions/subscribe`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys,
        }),
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Ошибка сохранения подписки: HTTP ${res.status} — ${err}`);
    }

    return res.json();
};

export const fetchVapidPublicKey = async () => {
    const res = await fetch(`${API_BASE_URL}/push-subscriptions/vapid-key`);
    if (!res.ok) throw new Error(`Не удалось получить VAPID ключ: HTTP ${res.status}`);
    const data = await res.json();
    return data.public_key;
};

export const subscribeToServerPush = async (knownVapidPublicKey = null) => {
    if (!hasServiceWorkerApi() || !hasPushApi()) {
        throw new Error('Push API не поддерживается в этом браузере.');
    }
    if (!isSecureForServiceWorker()) {
        throw new Error('Push-подписка требует HTTPS.');
    }
    if (!hasNotificationApi() || Notification.permission !== 'granted') {
        throw new Error('Сначала необходимо разрешить уведомления.');
    }

    const vapidPublicKey = knownVapidPublicKey || await fetchVapidPublicKey();
    const registration = await initNotificationServiceWorker();
    if (!registration) throw new Error('Service Worker не зарегистрирован.');

    const applicationServerKey = _urlBase64ToUint8Array(vapidPublicKey);
    const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
    });

    return savePushSubscriptionOnServer(pushSubscription);
};

export const unsubscribeFromServerPush = async () => {
    if (!hasServiceWorkerApi() || !hasPushApi()) return;

    // Explicit opt-out: stops the launch/foreground re-sync from silently re-subscribing.
    localStorage.setItem(PUSH_OPT_OUT_KEY, '1');

    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
    if (!registration) return;

    const pushSubscription = await registration.pushManager.getSubscription();
    if (!pushSubscription) return;

    const subscriptionJson = pushSubscription.toJSON();
    try {
        const headers = _authHeaders();
        await fetch(`${API_BASE_URL}/push-subscriptions/unsubscribe`, {
            method: 'DELETE',
            headers,
            body: JSON.stringify({
                endpoint: subscriptionJson.endpoint,
                keys: subscriptionJson.keys,
            }),
        });
    } catch {
        // best-effort
    }
    await pushSubscription.unsubscribe();
};

export const getServerPushSubscription = async () => {
    if (!hasServiceWorkerApi() || !hasPushApi()) return null;
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
    if (!registration) return null;
    return registration.pushManager.getSubscription();
};

export const serverSendToAll = async (payload = {}) => {
    const headers = _authHeaders();
    const res = await fetch(`${API_BASE_URL}/push-subscriptions/send-test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            title: payload.title || 'FPC CRM',
            body: payload.body || 'Тестовое уведомление',
            url: payload.url || '/dashboard',
        }),
    });
    if (!res.ok) {
        const err = await res.text();
        throw new Error(`HTTP ${res.status}: ${err}`);
    }
    return res.json();
};

export const serverSendToMe = async (payload = {}) => {
    const headers = _authHeaders();
    const res = await fetch(`${API_BASE_URL}/push-subscriptions/send-to-me`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            title: payload.title || 'FPC CRM',
            body: payload.body || 'Тестовое уведомление',
            url: payload.url || '/dashboard',
        }),
    });
    if (!res.ok) {
        const err = await res.text();
        throw new Error(`HTTP ${res.status}: ${err}`);
    }
    return res.json();
};

export const autoEnablePushNotifications = async () => {
    const diag = getNotificationDiagnostics();
    if (!diag.hasNotificationApi || !diag.hasServiceWorkerApi || !diag.hasPushApi) return { skipped: 'unsupported' };
    if (!diag.isSecureContext) return { skipped: 'insecure-context' };

    await initNotificationServiceWorker();

    if (localStorage.getItem(PUSH_OPT_OUT_KEY)) {
        return { skipped: 'user-disabled' };
    }

    if (diag.permission === 'denied') {
        return { skipped: 'permission-denied' };
    }

    if (diag.permission === 'default') {
        return { skipped: 'permission-user-action-required' };
    }

    if (Notification.permission !== 'granted') {
        return { skipped: 'permission-not-granted' };
    }

    const vapidPublicKey = await fetchVapidPublicKey();
    const existing = await getServerPushSubscription();
    if (existing) {
        if (_subscriptionUsesVapidKey(existing, vapidPublicKey)) {
            await savePushSubscriptionOnServer(existing);
            return { synchronized: true };
        }

        const subscriptionJson = existing.toJSON();
        try {
            const response = await fetch(`${API_BASE_URL}/push-subscriptions/unsubscribe`, {
                method: 'DELETE',
                headers: _authHeaders(),
                body: JSON.stringify({
                    endpoint: subscriptionJson.endpoint,
                    keys: subscriptionJson.keys,
                }),
            });
            if (!response.ok) {
                console.warn(`Не удалось удалить старую push-подписку: HTTP ${response.status}`);
            }
        } catch (error) {
            console.warn('Не удалось удалить старую push-подписку на сервере:', error);
        }
        await existing.unsubscribe();
    }

    await subscribeToServerPush(vapidPublicKey);
    return { subscribed: true, replaced: Boolean(existing) };
};

export const enablePushNotificationsFromUserGesture = async () => {
    const diagnostics = getNotificationDiagnostics();
    if (!diagnostics.hasNotificationApi || !diagnostics.hasServiceWorkerApi || !diagnostics.hasPushApi) {
        throw new Error('Push-уведомления не поддерживаются этим браузером.');
    }
    if (!diagnostics.isSecureContext) {
        throw new Error('Push-уведомления требуют HTTPS.');
    }

    let permission = Notification.permission;
    if (permission === 'default') {
        permission = await requestBrowserNotificationPermission();
    }
    if (permission !== 'granted') {
        throw new Error(
            permission === 'denied'
                ? 'Уведомления заблокированы в настройках устройства.'
                : 'Разрешение на уведомления не предоставлено.'
        );
    }

    const vapidPublicKey = await fetchVapidPublicKey();
    const registration = await initNotificationServiceWorker();
    if (!registration) {
        throw new Error('Service Worker не зарегистрирован.');
    }

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
        const subscriptionJson = existing.toJSON();
        try {
            const response = await fetch(`${API_BASE_URL}/push-subscriptions/unsubscribe`, {
                method: 'DELETE',
                headers: _authHeaders(),
                body: JSON.stringify({
                    endpoint: subscriptionJson.endpoint,
                    keys: subscriptionJson.keys,
                }),
            });
            if (!response.ok) {
                console.warn(`Не удалось удалить прежнюю push-подписку: HTTP ${response.status}`);
            }
        } catch (error) {
            console.warn('Не удалось удалить прежнюю push-подписку на сервере:', error);
        }

        const unsubscribed = await existing.unsubscribe();
        if (!unsubscribed) {
            throw new Error('Браузер не смог удалить прежнюю push-подписку.');
        }
    }

    const applicationServerKey = _urlBase64ToUint8Array(vapidPublicKey);
    const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
    });
    const savedSubscription = await savePushSubscriptionOnServer(pushSubscription);
    localStorage.removeItem(PUSH_OPT_OUT_KEY);

    return {
        subscribed: true,
        replaced: Boolean(existing),
        subscriptionId: savedSubscription.id,
    };
};

const resyncPushSubscription = async () => {
    if (!localStorage.getItem('token')) return;
    lastPushResyncAt = Date.now();
    try {
        await autoEnablePushNotifications();
    } catch (error) {
        console.warn('Не удалось синхронизировать push-подписку:', error);
    }
};

/**
 * Keeps the server's copy of the push subscription fresh while the app is open and forwards
 * in-app navigation requests from the service worker. Safe where push or service workers are
 * unsupported (e.g. Safari in a tab): autoEnablePushNotifications skips itself there.
 * Returns a cleanup function.
 */
export const watchPushAndServiceWorkerEvents = ({ onNavigate } = {}) => {
    if (!isBrowser()) return () => {};

    const handleVisibilityChange = () => {
        const isDue = Date.now() - lastPushResyncAt >= PUSH_RESYNC_MIN_INTERVAL_MS;
        if (document.visibilityState === 'visible' && isDue) {
            resyncPushSubscription();
        }
    };

    const handleWorkerMessage = (event) => {
        const { type, url } = event.data || {};
        if (type === 'FPC_PUSH_SUBSCRIPTION_CHANGED') {
            resyncPushSubscription();
        } else if (type === 'FPC_NAVIGATE' && typeof url === 'string' && url.startsWith('/') && onNavigate) {
            onNavigate(url);
            // Подтверждение: иначе service worker перезагрузит вкладку через navigate()
            event.ports?.[0]?.postMessage({ type: 'FPC_NAVIGATE_ACK' });
        }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (hasServiceWorkerApi()) {
        navigator.serviceWorker.addEventListener('message', handleWorkerMessage);
    }

    return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (hasServiceWorkerApi()) {
            navigator.serviceWorker.removeEventListener('message', handleWorkerMessage);
        }
    };
};
